<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="saveDomain">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ isEdit ? $t('Domain bearbeiten') : $t('Domain anlegen') }}</h1>
                    </div>
                    <div class="inline-flex items-center">
                        <div
                            v-if="isEdit"
                            ref="headerActionMenuWrapper"
                            class="relative inline-flex items-stretch mr-0"
                        >
                            <button class="btn-stage-primary !rounded-r-none !pr-3.5 inline-flex items-center gap-1.5" type="submit">
                                <span>{{ $t('Domain speichern') }}</span>
                            </button>
                            <button
                                ref="headerActionMenuTrigger"
                                class="btn-stage-primary !rounded-l-none border-l border-l-[rgba(255,255,255,0.16)] min-w-[2.25rem] !px-2 inline-flex items-center justify-center"
                                type="button"
                                :title="$t('Aktionen')"
                                :aria-label="$t('Aktionen')"
                                :aria-expanded="headerActionMenuOpen ? 'true' : 'false'"
                                @click.stop="toggleHeaderActionMenu"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                            </button>
                            <div
                                v-show="headerActionMenuOpen"
                                ref="headerActionMenu"
                                class="absolute right-0 top-full z-20 mt-2 whitespace-nowrap rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                            >
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('cancel')">
                                    {{ $t('Abbrechen') }}
                                </button>
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('delete')">
                                    {{ $t('Domain löschen') }}
                                </button>
                            </div>
                        </div>
                        <button v-else class="btn-stage-primary" type="submit">
                            {{ $t('Domain anlegen') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div class="space-y-6">
                    <app-panel :title="$t('Allgemein')">
                            <div class="flex items-center mb-6">
                                <input-switch
                                    id="cms-domain-active"
                                    v-model="domain.is_active"
                                    :disabled-label="$t('Domain aktivieren')"
                                    :enabled-label="$t('Domain aktivieren')"
                                />
                            </div>

                            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Name') }}</label>
                                    <input v-model="domain.name" class="stage-filter-input form-input block w-full text-sm">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Host') }}</label>
                                    <input v-model="domain.host" class="stage-filter-input form-input block w-full text-sm" required placeholder="www.beispiel.de">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Standardsprache') }}</label>
                                    <select v-model="domain.default_cms_language_id" class="stage-filter-input form-select block w-full text-sm">
                                        <option :value="null">{{ $t('Globale CMS-Standardsprache') }}</option>
                                        <option v-for="language in languageOptions" :key="'default-language-' + language.id" :value="language.id">
                                            {{ language.label }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                    </app-panel>

                    <app-panel :title="$t('Fallbacks')">
                            <div class="space-y-6">
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Unbekannte Pfade') }}</label>
                                    <select v-model="domain.unmapped_path_behavior" class="stage-filter-input form-select block w-full text-sm" required>
                                        <option
                                            v-for="option in unmappedPathBehaviorOptions"
                                            :key="option.value"
                                            :value="option.value"
                                        >
                                            {{ option.label }}
                                        </option>
                                    </select>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Standardseite') }}</label>
                                    <select v-model="domain.default_page_translation_id" class="stage-filter-input form-select block w-full text-sm">
                                        <option :value="null">{{ $t('Keine Standardseite') }}</option>
                                        <option v-for="translation in translationOptions" :key="'default-' + translation.id" :value="translation.id">
                                            {{ translation.label }}
                                        </option>
                                    </select>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Eigene 404-Seite') }}</label>
                                    <select v-model="domain.not_found_page_translation_id" class="stage-filter-input form-select block w-full text-sm">
                                        <option :value="null">{{ $t('Standard-404 verwenden') }}</option>
                                        <option v-for="translation in translationOptions" :key="'not-found-' + translation.id" :value="translation.id">
                                            {{ translation.label }}
                                        </option>
                                    </select>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Eigene 403-Seite') }}</label>
                                    <select v-model="domain.forbidden_page_translation_id" class="stage-filter-input form-select block w-full text-sm">
                                        <option :value="null">{{ $t('Standard-403 verwenden') }}</option>
                                        <option v-for="translation in translationOptions" :key="'forbidden-' + translation.id" :value="translation.id">
                                            {{ translation.label }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                    </app-panel>

                    <app-panel body-class="cms-route-panel-body">
                        <template #header>
                                <div>
                                    <div class="stage-panel-header-label">{{ $t('Pfad-Zuordnungen') }}</div>
                                    <div v-if="requiresLanguagePrefixedPaths" class="mt-1 text-xs text-gray-500">
                                        {{ $t('Sprachpräfixe wie /de/... oder /en/... sind optional. Wenn ein Präfix gesetzt wird, muss es zur ausgewählten Sprache der Zeile passen.') }}
                                    </div>
                                </div>
                        </template>
                        <template #header-actions>
                                <button class="btn-stage-secondary text-xs" type="button" @click="addRoute">
                                    {{ $t('Pfad hinzufügen') }}
                                </button>
                        </template>
                            <div v-if="domain.routes.length > 0" class="overflow-auto">
                                <table class="table-listview table-listview--zebra cms-route-table">
                                    <thead>
                                    <tr>
                                        <th class="table-listview-l-th whitespace-nowrap w-[28rem]">{{ $t('Pfad') }}</th>
                                        <th class="table-listview-l-th w-[30rem]">{{ $t('Seite') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-56">{{ $t('Sprache') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-32">{{ $t('Status') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-28">
                                            <div class="w-full text-right">{{ $t('Aktionen') }}</div>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody class="bg-white">
                                    <tr v-for="(route, index) in domain.routes" :key="'route-' + index">
                                        <td class="table-listview-l-td align-middle">
                                            <input
                                                v-model="route.request_path"
                                                class="stage-filter-input form-input block w-full text-sm"
                                                required
                                                :placeholder="routePathPlaceholder(route)"
                                            >
                                        </td>
                                        <td class="table-listview-l-td align-middle">
                                            <select v-model="route.cms_page_id" class="stage-filter-input form-select block w-full text-sm" required @change="syncRouteLanguage(route)">
                                                <option :value="null">{{ $t('Seite wählen') }}</option>
                                                <option v-for="page in pageOptions" :key="page.id" :value="page.id">
                                                    {{ page.label }}
                                                </option>
                                            </select>
                                        </td>
                                        <td class="table-listview-l-td align-middle">
                                            <select v-model="route.cms_language_id" class="stage-filter-input form-select block w-full text-sm" required @change="syncRouteTranslation(route)">
                                                <option :value="null">{{ $t('Sprache wählen') }}</option>
                                                <option
                                                    v-for="language in availableLanguagesForRoute(route)"
                                                    :key="language.id"
                                                    :value="language.id"
                                                >
                                                    {{ language.label }}
                                                </option>
                                            </select>
                                        </td>
                                        <td class="table-listview-l-td align-middle whitespace-nowrap text-left">
                                            <label class="inline-flex items-center text-sm text-gray-700">
                                                <input
                                                    :id="'cms-domain-route-' + index"
                                                    v-model="route.is_active"
                                                    class="form-checkbox rounded border-gray-300 text-gray-800 focus:ring-gray-300"
                                                    type="checkbox"
                                                >
                                            </label>
                                        </td>
                                        <td class="table-listview-l-td align-middle whitespace-nowrap text-right">
                                            <div class="inline-flex w-full items-center justify-end">
                                                <button
                                                    class="inline-flex items-center justify-center text-gray-400 hover:text-gray-700 focus:outline-none"
                                                    type="button"
                                                    :title="$t('Aktionen')"
                                                    :aria-label="$t('Aktionen')"
                                                    :data-domain-route-action-trigger="index"
                                                    @click.stop="toggleRouteActionMenu(index, route, $event)"
                                                >
                                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div v-else class="p-6 text-sm text-gray-500">
                                {{ $t('Noch keine Pfad-Zuordnungen vorhanden.') }}
                            </div>
                    </app-panel>

                    <app-panel :title="$t('Weiterleitungen')" body-class="cms-route-panel-body">
                        <template #header-actions>
                            <button class="btn-stage-secondary text-xs" type="button" @click="addRedirect">
                                {{ $t('Weiterleitung hinzufügen') }}
                            </button>
                        </template>
                            <div v-if="domain.redirects.length > 0" class="overflow-auto">
                                <table class="table-listview table-listview--zebra cms-route-table cms-redirect-table">
                                    <thead>
                                    <tr>
                                        <th class="table-listview-l-th whitespace-nowrap w-[20rem]">{{ $t('Quellpfad') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-44">{{ $t('Zieltyp') }}</th>
                                        <th class="table-listview-l-th w-[30rem]">{{ $t('Ziel') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-44">{{ $t('Weiterleitung') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-32">{{ $t('Status') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-28">
                                            <div class="w-full text-right">{{ $t('Aktionen') }}</div>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody class="bg-white">
                                    <tr v-for="(redirect, index) in domain.redirects" :key="'redirect-' + index">
                                        <td class="table-listview-l-td align-middle">
                                            <input v-model="redirect.source_path" class="stage-filter-input form-input block w-full text-sm" required placeholder="/alter-pfad">
                                        </td>
                                        <td class="table-listview-l-td align-middle">
                                            <select v-model="redirect.target_type" class="stage-filter-input form-select block w-full text-sm" required @change="syncRedirectTarget(redirect)">
                                                <option
                                                    v-for="option in redirectTargetTypeOptions"
                                                    :key="'redirect-target-type-' + option.value"
                                                    :value="option.value"
                                                >
                                                    {{ option.label }}
                                                </option>
                                            </select>
                                        </td>
                                        <td class="table-listview-l-td align-middle">
                                            <select
                                                v-if="redirect.target_type === 'page'"
                                                v-model="redirect.cms_page_translation_id"
                                                class="stage-filter-input form-select block w-full text-sm"
                                                required
                                            >
                                                <option :value="null">{{ $t('Zielseite wählen') }}</option>
                                                <option
                                                    v-for="translation in redirectTargetPageOptions"
                                                    :key="'redirect-target-page-' + translation.id"
                                                    :value="translation.id"
                                                >
                                                    {{ translation.label }}
                                                </option>
                                            </select>
                                            <input
                                                v-else
                                                v-model="redirect.target_url"
                                                class="stage-filter-input form-input block w-full text-sm"
                                                required
                                                placeholder="https://www.beispiel.de/neuer-pfad"
                                            >
                                        </td>
                                        <td class="table-listview-l-td align-middle">
                                            <select v-model="redirect.http_status" class="stage-filter-input form-select block w-full text-sm" required>
                                                <option
                                                    v-for="option in redirectHttpStatusOptions"
                                                    :key="'redirect-http-status-' + option.value"
                                                    :value="option.value"
                                                >
                                                    {{ option.label }}
                                                </option>
                                            </select>
                                        </td>
                                        <td class="table-listview-l-td align-middle whitespace-nowrap text-left">
                                            <label class="inline-flex items-center text-sm text-gray-700">
                                                <input
                                                    :id="'cms-domain-redirect-' + index"
                                                    v-model="redirect.is_active"
                                                    class="form-checkbox rounded border-gray-300 text-gray-800 focus:ring-gray-300"
                                                    type="checkbox"
                                                >
                                            </label>
                                        </td>
                                        <td class="table-listview-l-td align-middle whitespace-nowrap text-right">
                                            <div class="inline-flex w-full items-center justify-end">
                                                <button
                                                    class="inline-flex items-center justify-center text-gray-400 hover:text-gray-700 focus:outline-none"
                                                    type="button"
                                                    :title="$t('Aktionen')"
                                                    :aria-label="$t('Aktionen')"
                                                    :data-domain-redirect-action-trigger="index"
                                                    @click.stop="toggleRedirectActionMenu(index, redirect, $event)"
                                                >
                                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div v-else class="p-6 text-sm text-gray-500">
                                {{ $t('Noch keine Weiterleitungen vorhanden.') }}
                            </div>
                    </app-panel>

                    <div class="grid grid-cols-1 gap-6">
                        <app-panel :title="$t('.htaccess for Apache')" body-class="cms-code-preview-body">
                            <code-preview-editor :model-value="htaccessPreview" fill />
                        </app-panel>

                        <app-panel
                            :title="$t('PHP snippet for the external server')"
                            body-class="cms-code-preview-body cms-code-preview-body--large"
                        >
                            <code-preview-editor :model-value="snippetPreview" fill />
                            <template #footer>
                                <div class="flex flex-col gap-4 lg:flex-row lg:items-end">
                                    <div class="flex-1 min-w-0">
                                        <div class="cms-token-input-group">
                                            <div class="cms-token-input-label">{{ $t('Token') }}</div>
                                            <div class="cms-token-input-field-wrap">
                                                <input v-model="domain.access_token" class="stage-filter-input form-input block w-full pr-12 text-sm cms-token-input-field" required>
                                                <button
                                                    class="absolute inset-y-0 right-0 inline-flex items-center justify-center px-3 text-gray-600"
                                                    type="button"
                                                    :title="$t('Token neu generieren')"
                                                    :aria-label="$t('Token neu generieren')"
                                                    @click="generateToken"
                                                >
                                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-rotate-right"></svg-vue>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </app-panel>
                    </div>
                </div>
            </div>
        </form>
        <confirm-dialog
            :open="confirmDialog.open"
            :title="confirmDialog.title"
            :text="confirmDialog.text"
            :headline-id="'cms-domain-confirm-modal-headline'"
            @close="closeConfirmDialog"
            @confirm="confirmDialogAction"
        />

        <div
            v-if="routeActionMenu.index !== null"
            ref="routeActionMenu"
            class="fixed z-[9999] w-44 rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
            :style="routeActionMenuStyle"
            @click.stop
        >
            <a
                v-if="routePreviewUrl(routeActionMenu.route)"
                :href="routePreviewUrl(routeActionMenu.route)"
                class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                rel="noopener noreferrer"
                target="_blank"
                @click="closeRouteActionMenu"
            >
                {{ $t('Pfad öffnen') }}
            </a>
            <button
                class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                type="button"
                @click.stop="removeActionMenuRoute"
            >
                {{ $t('Pfad entfernen') }}
            </button>
        </div>

        <div
            v-if="redirectActionMenu.index !== null"
            ref="redirectActionMenu"
            class="fixed z-[9999] w-48 rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
            :style="redirectActionMenuStyle"
            @click.stop
        >
            <a
                v-if="redirectPreviewUrl(redirectActionMenu.redirect)"
                :href="redirectPreviewUrl(redirectActionMenu.redirect)"
                class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                rel="noopener noreferrer"
                target="_blank"
                @click="closeRedirectActionMenu"
            >
                {{ $t('Weiterleitung öffnen') }}
            </a>
            <button
                class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                type="button"
                @click.stop="removeActionMenuRedirect"
            >
                {{ $t('Weiterleitung entfernen') }}
            </button>
        </div>
    </main>
</template>

<script>
import CodePreviewEditor from "@/components/forms/code-preview-editor";
import ConfirmDialog from "@/components/modals/confirm-dialog";

export default {
    name: "cms-domain-editor",
    components: {
        CodePreviewEditor,
        ConfirmDialog,
    },
    data() {
        return {
            loading: false,
            confirmDialog: {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            },
            headerActionMenuOpen: false,
            routeActionMenu: {
                index: null,
                route: null,
                top: 0,
                left: 0,
                openUpward: false,
            },
            redirectActionMenu: {
                index: null,
                redirect: null,
                top: 0,
                left: 0,
                openUpward: false,
            },
            translationOptions: [],
            pageOptions: [],
            languageOptions: [],
            unmappedPathBehaviorOptions: [],
            redirectTargetTypeOptions: [],
            redirectHttpStatusOptions: [],
            domain: {
                name: '',
                host: '',
                access_token: '',
                default_cms_language_id: null,
                unmapped_path_behavior: '404',
                default_page_translation_id: null,
                not_found_page_translation_id: null,
                forbidden_page_translation_id: null,
                is_active: true,
                routes: [],
                redirects: [],
            },
        };
    },
    computed: {
        isEdit() {
            return !!this.$route.params.id;
        },
        routeActionMenuStyle() {
            const transform = this.routeActionMenu.openUpward
                ? 'translate(-100%, calc(-100% - 8px))'
                : 'translate(-100%, 0)';

            return {
                top: this.routeActionMenu.openUpward ? `${this.routeActionMenu.top}px` : `${this.routeActionMenu.top + 8}px`,
                left: `${this.routeActionMenu.left}px`,
                transform,
            };
        },
        redirectActionMenuStyle() {
            const transform = this.redirectActionMenu.openUpward
                ? 'translate(-100%, calc(-100% - 8px))'
                : 'translate(-100%, 0)';

            return {
                top: this.redirectActionMenu.openUpward ? `${this.redirectActionMenu.top}px` : `${this.redirectActionMenu.top + 8}px`,
                left: `${this.redirectActionMenu.left}px`,
                transform,
            };
        },
        redirectTargetPageOptions() {
            const mappedIds = new Set();

            (Array.isArray(this.domain.routes) ? this.domain.routes : []).forEach((route) => {
                const translationId = Number(route.cms_page_translation_id || this.resolveTranslationId(route.cms_page_id, route.cms_language_id) || 0);
                if (translationId > 0 && !!route.is_active) {
                    mappedIds.add(translationId);
                }
            });

            return this.translationOptions.filter((translation) => mappedIds.has(Number(translation.id)));
        },
        activeRouteLanguageIds() {
            return Array.from(new Set(
                (Array.isArray(this.domain.routes) ? this.domain.routes : [])
                    .filter((route) => Boolean(route.is_active))
                    .map((route) => Number(route.cms_language_id || 0))
                    .filter((languageId) => languageId > 0)
            ));
        },
        requiresLanguagePrefixedPaths() {
            return this.activeRouteLanguageIds.length > 1;
        },
        renderBaseUrl() {
            const configuredBaseUrl = String(window.app && window.app.url ? window.app.url : '').trim();
            const fallbackBaseUrl = window.location.origin;
            const baseUrl = configuredBaseUrl !== '' ? configuredBaseUrl : fallbackBaseUrl;

            return baseUrl.replace(/\/+$/, '');
        },
        snippetPreview() {
            const host = this.domain.host || 'www.beispiel.de';
            const token = this.domain.access_token || 'TOKEN_HIER';
            const renderUrl = this.renderBaseUrl + '/cms/render';
            return `<?php\n$path = parse_url($_SERVER['REQUEST_URI'] ?? '/', PHP_URL_PATH) ?: '/';\n$endpoint = '${renderUrl}?host=' . urlencode('${host}') . '&path=' . urlencode($path) . '&token=' . urlencode('${token}');\n$context = stream_context_create(['http' => ['ignore_errors' => true]]);\n$html = @file_get_contents($endpoint, false, $context);\n$status = 200;\n$location = null;\nforeach (($http_response_header ?? []) as $headerLine) {\n    if (preg_match('/^HTTP\\/\\S+\\s+(\\d+)/i', $headerLine, $matches)) {\n        $status = (int) $matches[1];\n    }\n    if (stripos($headerLine, 'Location:') === 0) {\n        $location = trim(substr($headerLine, 9));\n    }\n}\nif ($location && in_array($status, [301, 302], true)) {\n    header('Location: ' . $location, true, $status);\n    exit;\n}\nif ($html === false) { http_response_code(503); exit('CMS not reachable'); }\nhttp_response_code($status);\necho $html;\n`;
        },
        htaccessPreview() {
            return `RewriteEngine On\n\nRewriteCond %{REQUEST_FILENAME} !-f\nRewriteCond %{REQUEST_FILENAME} !-d\nRewriteRule ^ index.php [L,QSA]\n`;
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
        window.addEventListener('resize', this.closeRouteActionMenu);
        window.addEventListener('resize', this.closeRedirectActionMenu);
        window.addEventListener('scroll', this.closeRouteActionMenu, true);
        window.addEventListener('scroll', this.closeRedirectActionMenu, true);
        this.loadOptions();
        if (this.isEdit) {
            this.getDomain();
        } else {
            this.generateToken();
        }
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
        window.removeEventListener('resize', this.closeRouteActionMenu);
        window.removeEventListener('resize', this.closeRedirectActionMenu);
        window.removeEventListener('scroll', this.closeRouteActionMenu, true);
        window.removeEventListener('scroll', this.closeRedirectActionMenu, true);
    },
    methods: {
        closeHeaderActionMenu() {
            this.headerActionMenuOpen = false;
        },
        toggleHeaderActionMenu() {
            this.headerActionMenuOpen = !this.headerActionMenuOpen;
        },
        runHeaderAction(action) {
            this.closeHeaderActionMenu();

            if (action === 'cancel') {
                this.$router.push('/workspace/cms/domains');
                return;
            }

            if (action === 'delete') {
                this.deleteDomain();
            }
        },
        loadOptions() {
            axios.get('api/workspace/admin/cms/domains/options').then((response) => {
                this.translationOptions = Array.isArray(response.data.translations) ? response.data.translations : [];
                this.pageOptions = Array.isArray(response.data.pages) ? response.data.pages : [];
                this.languageOptions = Array.isArray(response.data.languages) ? response.data.languages : [];
                this.unmappedPathBehaviorOptions = Array.isArray(response.data.unmapped_path_behaviors) ? response.data.unmapped_path_behaviors : [];
                this.redirectTargetTypeOptions = Array.isArray(response.data.redirect_target_types) ? response.data.redirect_target_types : [];
                this.redirectHttpStatusOptions = Array.isArray(response.data.redirect_http_statuses) ? response.data.redirect_http_statuses : [];
            });
        },
        getDomain() {
            this.loading = true;
            axios.get('api/workspace/admin/cms/domains/' + this.$route.params.id).then((response) => {
                this.domain = response.data;
                this.domain.routes = (Array.isArray(this.domain.routes) ? this.domain.routes : []).map((route) => ({
                    ...route,
                    cms_page_id: route.cms_page_id || route.translation?.page_id || null,
                    cms_language_id: route.cms_language_id || route.translation?.cms_language_id || null,
                }));
                this.domain.redirects = (Array.isArray(this.domain.redirects) ? this.domain.redirects : []).map((redirect) => ({
                    ...redirect,
                    cms_page_translation_id: redirect.cms_page_translation_id || redirect.translation?.id || null,
                    target_type: redirect.target_type || 'page',
                    target_url: redirect.target_url || '',
                    http_status: Number(redirect.http_status || 301),
                }));
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
        saveDomain() {
            const payload = {
                ...this.domain,
                routes: (Array.isArray(this.domain.routes) ? this.domain.routes : []).map((route) => ({
                    id: route.id || null,
                    request_path: route.request_path,
                    is_active: !!route.is_active,
                    cms_page_translation_id: this.resolveTranslationId(route.cms_page_id, route.cms_language_id),
                })),
                redirects: (Array.isArray(this.domain.redirects) ? this.domain.redirects : []).map((redirect) => ({
                    id: redirect.id || null,
                    source_path: redirect.source_path,
                    target_type: redirect.target_type,
                    cms_page_translation_id: redirect.target_type === 'page' ? Number(redirect.cms_page_translation_id || 0) || null : null,
                    target_url: redirect.target_type === 'url' ? redirect.target_url : '',
                    http_status: Number(redirect.http_status || 301),
                    is_active: !!redirect.is_active,
                })),
            };
            const request = this.isEdit
                ? axios.put('api/workspace/admin/cms/domains/' + this.$route.params.id, payload)
                : axios.post('api/workspace/admin/cms/domains', payload);

            request.then((response) => {
                this.$notify({title: this.$t('Success'), text: this.isEdit ? this.$t('Data updated correctly') : this.$t('Data saved correctly'), type: 'success'});
                if (!this.isEdit) {
                    this.$router.push('/workspace/cms/domains/' + response.data.domain.id + '/edit');
                } else {
                    this.domain = response.data.domain;
                    this.domain.routes = (Array.isArray(this.domain.routes) ? this.domain.routes : []).map((route) => ({
                        ...route,
                        cms_page_id: route.cms_page_id || route.translation?.page_id || null,
                        cms_language_id: route.cms_language_id || route.translation?.cms_language_id || null,
                    }));
                    this.domain.redirects = (Array.isArray(this.domain.redirects) ? this.domain.redirects : []).map((redirect) => ({
                        ...redirect,
                        cms_page_translation_id: redirect.cms_page_translation_id || redirect.translation?.id || null,
                        target_type: redirect.target_type || 'page',
                        target_url: redirect.target_url || '',
                        http_status: Number(redirect.http_status || 301),
                    }));
                }
            });
        },
        deleteDomain() {
            this.openConfirmDialog({
                title: this.$t('Domain löschen'),
                text: this.$t('Soll diese Domain wirklich gelöscht werden?'),
                pendingAction: () => {
                    axios.delete('api/workspace/admin/cms/domains/' + this.$route.params.id).then(() => {
                        this.$notify({title: this.$t('Success'), text: this.$t('Data deleted successfully'), type: 'success'});
                        this.$router.push('/workspace/cms/domains');
                    });
                },
            });
        },
        addRoute() {
            this.domain.routes.push({
                cms_page_translation_id: null,
                cms_page_id: null,
                cms_language_id: null,
                request_path: '/',
                is_active: true,
            });
        },
        addRedirect() {
            this.domain.redirects.push({
                cms_page_translation_id: null,
                source_path: '/',
                target_type: 'page',
                target_url: '',
                http_status: 301,
                is_active: true,
            });
            this.$nextTick(() => {
                const redirect = this.domain.redirects[this.domain.redirects.length - 1];
                if (redirect) {
                    this.syncRedirectTarget(redirect);
                }
            });
        },
        removeRoute(index) {
            this.closeRouteActionMenu();
            this.openConfirmDialog({
                title: this.$t('Pfad entfernen'),
                text: this.$t('Soll diese Pfad-Zuordnung wirklich entfernt werden?'),
                pendingAction: () => {
                    this.domain.routes.splice(index, 1);
                },
            });
        },
        removeRedirect(index) {
            this.closeRedirectActionMenu();
            this.openConfirmDialog({
                title: this.$t('Weiterleitung entfernen'),
                text: this.$t('Soll diese Weiterleitung wirklich entfernt werden?'),
                pendingAction: () => {
                    this.domain.redirects.splice(index, 1);
                },
            });
        },
        toggleRouteActionMenu(index, route, event) {
            if (this.routeActionMenu.index === index) {
                this.closeRouteActionMenu();
                return;
            }

            this.closeRedirectActionMenu();
            const rect = event.currentTarget.getBoundingClientRect();
            const estimatedHeight = this.routePreviewUrl(route) ? 96 : 52;
            const hasSpaceBelow = (window.innerHeight - rect.bottom) >= (estimatedHeight + 16);

            this.routeActionMenu = {
                index,
                route,
                top: hasSpaceBelow ? rect.bottom : rect.top,
                left: rect.right,
                openUpward: !hasSpaceBelow,
            };
        },
        toggleRedirectActionMenu(index, redirect, event) {
            if (this.redirectActionMenu.index === index) {
                this.closeRedirectActionMenu();
                return;
            }

            this.closeRouteActionMenu();
            const rect = event.currentTarget.getBoundingClientRect();
            const estimatedHeight = this.redirectPreviewUrl(redirect) ? 96 : 52;
            const hasSpaceBelow = (window.innerHeight - rect.bottom) >= (estimatedHeight + 16);

            this.redirectActionMenu = {
                index,
                redirect,
                top: hasSpaceBelow ? rect.bottom : rect.top,
                left: rect.right,
                openUpward: !hasSpaceBelow,
            };
        },
        closeRouteActionMenu() {
            this.routeActionMenu = {
                index: null,
                route: null,
                top: 0,
                left: 0,
                openUpward: false,
            };
        },
        closeRedirectActionMenu() {
            this.redirectActionMenu = {
                index: null,
                redirect: null,
                top: 0,
                left: 0,
                openUpward: false,
            };
        },
        handleDocumentClick(event) {
            if (this.headerActionMenuOpen) {
                const menuElement = this.$refs.headerActionMenu;
                const triggerElement = this.$refs.headerActionMenuTrigger;
                const wrapperElement = this.$refs.headerActionMenuWrapper;

                if (!(menuElement && menuElement.contains(event.target))
                    && !(triggerElement && triggerElement.contains(event.target))
                    && !(wrapperElement && wrapperElement.contains(event.target))) {
                    this.closeHeaderActionMenu();
                }
            }

            if (this.routeActionMenu.index !== null) {
                const menuElement = this.$refs.routeActionMenu;
                const triggerSelector = `[data-domain-route-action-trigger="${this.routeActionMenu.index}"]`;
                const triggerElement = event.target.closest(triggerSelector);

                if (!(menuElement && menuElement.contains(event.target)) && !triggerElement) {
                    this.closeRouteActionMenu();
                }
            }

            if (this.redirectActionMenu.index !== null) {
                const menuElement = this.$refs.redirectActionMenu;
                const triggerSelector = `[data-domain-redirect-action-trigger="${this.redirectActionMenu.index}"]`;
                const triggerElement = event.target.closest(triggerSelector);

                if (!(menuElement && menuElement.contains(event.target)) && !triggerElement) {
                    this.closeRedirectActionMenu();
                }
            }
        },
        removeActionMenuRoute() {
            if (this.routeActionMenu.index === null) {
                return;
            }

            this.removeRoute(this.routeActionMenu.index);
        },
        removeActionMenuRedirect() {
            if (this.redirectActionMenu.index === null) {
                return;
            }

            this.removeRedirect(this.redirectActionMenu.index);
        },
        routePreviewUrl(route) {
            const host = String(this.domain.host || '').trim();
            const path = String(route && route.request_path ? route.request_path : '').trim();

            if (host === '' || path === '') {
                return null;
            }

            const normalizedPath = path.startsWith('/') ? path : '/' + path;

            return 'https://' + host + normalizedPath;
        },
        redirectPreviewUrl(redirect) {
            const host = String(this.domain.host || '').trim();
            const path = String(redirect && redirect.source_path ? redirect.source_path : '').trim();

            if (host === '' || path === '') {
                return null;
            }

            const normalizedPath = path.startsWith('/') ? path : '/' + path;

            return 'https://' + host + normalizedPath;
        },
        routePathPlaceholder(route) {
            if (!this.requiresLanguagePrefixedPaths) {
                return '/impressum';
            }

            const language = this.languageOptions.find((option) => Number(option.id) === Number(route.cms_language_id || 0));
            const locale = String(language && language.locale ? language.locale : '').trim().toLowerCase();

            return locale !== '' ? `/${locale}/impressum` : '/de/impressum';
        },
        availableLanguagesForRoute(route) {
            const pageId = Number(route.cms_page_id || 0);
            const availableLanguageIds = new Set(
                this.translationOptions
                    .filter((translation) => Number(translation.page_id) === pageId)
                    .map((translation) => Number(translation.cms_language_id))
            );

            return this.languageOptions.filter((language) => availableLanguageIds.has(Number(language.id)));
        },
        syncRouteLanguage(route) {
            const languages = this.availableLanguagesForRoute(route);
            const currentLanguageId = Number(route.cms_language_id || 0);
            const hasCurrentLanguage = languages.some((language) => Number(language.id) === currentLanguageId);
            route.cms_language_id = hasCurrentLanguage
                ? currentLanguageId
                : (languages[0] ? Number(languages[0].id) : null);
            this.syncRouteTranslation(route);
        },
        syncRouteTranslation(route) {
            route.cms_page_translation_id = this.resolveTranslationId(route.cms_page_id, route.cms_language_id);
        },
        syncRedirectTarget(redirect) {
            if (redirect.target_type === 'url') {
                redirect.cms_page_translation_id = null;
                return;
            }

            redirect.target_url = '';
            const availableIds = new Set(this.redirectTargetPageOptions.map((translation) => Number(translation.id)));
            const currentId = Number(redirect.cms_page_translation_id || 0);
            if (!availableIds.has(currentId)) {
                redirect.cms_page_translation_id = this.redirectTargetPageOptions[0]
                    ? Number(this.redirectTargetPageOptions[0].id)
                    : null;
            }
        },
        resolveTranslationId(pageId, languageId) {
            const match = this.translationOptions.find((translation) =>
                Number(translation.page_id) === Number(pageId)
                && Number(translation.cms_language_id) === Number(languageId)
            );

            return match ? Number(match.id) : null;
        },
        generateToken() {
            this.domain.access_token = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
        },
        openConfirmDialog({title, text, pendingAction}) {
            this.confirmDialog = {
                open: true,
                title,
                text,
                pendingAction,
            };
        },
        closeConfirmDialog() {
            this.confirmDialog = {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            };
        },
        confirmDialogAction() {
            const action = this.confirmDialog.pendingAction;
            this.closeConfirmDialog();
            if (typeof action === 'function') {
                action();
            }
        },
    },
}
</script>

<style scoped>
.cms-code-preview-body {
    padding: 0 !important;
    height: 12rem;
    overflow: hidden;
}

.cms-code-preview-body--large {
    height: 16rem;
}

.cms-route-panel-body {
    padding: 0 !important;
    overflow: visible;
}

.cms-route-table .table-listview-l-th,
.cms-route-table .table-listview-l-td {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
}

.cms-route-table .table-listview-l-th:first-child,
.cms-route-table .table-listview-l-td:first-child {
    padding-right: 0.5rem;
}

.cms-route-table .table-listview-l-th:nth-child(2),
.cms-route-table .table-listview-l-td:nth-child(2) {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
}

.cms-route-table .table-listview-l-th:nth-child(3),
.cms-route-table .table-listview-l-td:nth-child(3) {
    padding-left: 0.5rem;
    padding-right: 0.875rem;
}

.cms-route-table .table-listview-l-th:nth-child(4),
.cms-route-table .table-listview-l-td:nth-child(4) {
    padding-left: 0.875rem;
    padding-right: 0.625rem;
}

.cms-route-table .table-listview-l-th:nth-child(5),
.cms-route-table .table-listview-l-td:nth-child(5) {
    padding-left: 0.625rem;
    padding-right: 1.5rem;
}

.cms-route-table .table-listview-l-td:nth-child(5) > div {
    justify-content: flex-end;
}

.cms-route-table .table-listview-l-td {
    padding-top: 0.875rem;
    padding-bottom: 0.875rem;
}

.cms-redirect-table .table-listview-l-th:nth-child(4),
.cms-redirect-table .table-listview-l-td:nth-child(4) {
    padding-left: 0.5rem;
    padding-right: 0.75rem;
}

.cms-redirect-table .table-listview-l-th:nth-child(5),
.cms-redirect-table .table-listview-l-td:nth-child(5) {
    padding-left: 0.75rem;
    padding-right: 0.625rem;
}

.cms-redirect-table .table-listview-l-th:nth-child(6),
.cms-redirect-table .table-listview-l-td:nth-child(6) {
    padding-left: 0.625rem;
    padding-right: 1.5rem;
}

.cms-redirect-table .table-listview-l-td:nth-child(6) > div {
    justify-content: flex-end;
}

.cms-token-input-group {
    display: flex;
    align-items: stretch;
    width: 100%;
    border: 1px solid var(--ui-border-default);
    border-radius: 0.375rem;
    overflow: hidden;
    background: #ffffff;
}

.cms-token-input-label {
    display: inline-flex;
    align-items: center;
    padding: 0 1rem;
    background: var(--ui-surface-raised);
    border-right: 1px solid var(--ui-border-subtle);
    color: var(--ui-text-muted);
    font-size: 0.875rem;
    line-height: 1.25rem;
    white-space: nowrap;
}

.cms-token-input-field-wrap {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
}

.cms-token-input-field {
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
}

.cms-token-input-field:focus {
    box-shadow: none !important;
}

</style>
